import {
  CircularLoader,
  ErrorPageLayout,
  LoadingPageLayout,
} from '../../../presentation';
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  EventNoticeDagIdEnum,
  StatusDagIdEnum,
  StatusTypeEnum,
  TargetAreaType,
  TargetType,
  statusValueToStatusInfo,
  useShouldPoll,
  useStatusesQuery,
  useStatusesSubscription,
} from '@converge/graphql';

import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';

interface IProps {
  children: ReactNode;
  onDone?: () => void;
}

export const BlockUntilAutoQuoteFinishes = ({ children, onDone }: IProps) => {
  const { updateStatus } = useStatusesSubscription();
  const {
    params: { submissionUuid },
  } = useUnderwritingSubmissionDetailPage();

  const [isAutoQuoteDone, setIsAutoQuoteDone] = useState(false);
  const onDoneCalledRef = useRef(false);

  useEffect(() => {
    if (
      updateStatus?.noticeDagId === EventNoticeDagIdEnum.Renewal &&
      updateStatus?.noticeValue === 'completed'
    ) {
      setIsAutoQuoteDone(true);
    }
  }, [updateStatus]);

  const { poll, setPoll } = useShouldPoll();

  const {
    info: { statuses },
    loading: statusesLoading,
  } = useStatusesQuery({
    operationName: 'autoQuote',
    poll,
    filters: {
      targetUuid: submissionUuid,
      targetArea: TargetAreaType.Submission,
      targetName: 'status',
      details: false,
      statusType: StatusTypeEnum.Renewal,
      statusDagId: StatusDagIdEnum.Renewal,
      targetType: TargetType.Node,
    },
  });

  const status = (statuses ?? [])[0];
  const autoQuote = statusValueToStatusInfo(status?.statusValue);
  const lastUpdatedAtText = status
    ? `Last updated at ${status.updatedAt}`
    : 'No updates yet';

  const isFinished = isAutoQuoteDone || autoQuote.finished;

  useEffect(() => {
    setPoll(!isFinished);
  }, [isFinished, setPoll]);

  useEffect(() => {
    if (isFinished && onDone && !onDoneCalledRef.current) {
      onDoneCalledRef.current = true;
      onDone();
    }
  }, [isFinished, onDone]);

  if (statusesLoading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  if (autoQuote.failed) {
    return (
      <ErrorPageLayout
        message="Auto quote failed"
        messageId="underwriting.autoQuote.error.message"
        subtitle="Please contact support for information."
        subtitleId="underwriting.autoQuote.error.subtitle"
        subtitleTooltip={lastUpdatedAtText}
      />
    );
  }

  if (isFinished) {
    return <>{children}</>;
  }

  return (
    <LoadingPageLayout
      title={
        autoQuote.pending
          ? 'Auto quote is pending...'
          : 'Generating auto quote...'
      }
      titleId={
        autoQuote.pending
          ? 'underwriting.autoQuote.pending.title'
          : 'underwriting.autoQuote.loading.title'
      }
      subtitle={
        poll ? 'Please wait a moment' : 'Please refresh the page periodically'
      }
      subtitleId={
        autoQuote.pending
          ? 'underwriting.autoQuote.pending.subtitle'
          : 'underwriting.autoQuote.loading.subtitle'
      }
      subtitleTooltip={lastUpdatedAtText}
      loading={statusesLoading}
    />
  );
};
